import { useEffect, useState } from "react";
import { supabase } from "../supabase";

const C = {
  bg:"#0e0e0e", surface:"#181818", surface2:"#202020",
  border:"#2a2a2a", muted:"#666", orange:"#FF5F1F", magenta:"#E8197D",
  gold:"#F5A623", green:"#4ade80", white:"#fff", red:"#f87171", blue:"#60a5fa",
};

const card  = (x={}) => ({ background:C.surface, borderRadius:10, padding:16, border:`0.5px solid ${C.border}`, ...x });
const btn   = (v="primary",x={}) => ({ padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", border:"none", boxSizing:"border-box", ...(v==="ghost"?{background:"none",border:`0.5px solid ${C.border}`,color:C.muted}:{}), ...(v==="primary"?{background:`linear-gradient(135deg,${C.orange},${C.magenta})`,color:C.white}:{}), ...x });
const inp   = (x={}) => ({ background:C.surface2, border:`0.5px solid ${C.border}`, borderRadius:8, padding:"9px 12px", color:C.white, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box", ...x });
const gradBar = <div style={{height:2,background:`linear-gradient(90deg,${C.orange},${C.magenta},${C.gold})`,borderRadius:1,marginBottom:24}}/>;

// ── Handlinger og ikoner ──
const ACTION_CFG = {
  login:              { label:"Logget inn",              icon:"🔑", color:C.green  },
  logout:             { label:"Logget ut",               icon:"🚪", color:C.muted  },
  page_view:          { label:"Besøkte side",            icon:"👁",  color:C.blue   },
  event_opened:       { label:"Åpnet event",             icon:"📋", color:C.orange },
  invite_accepted:    { label:"Aksepterte invitasjon",   icon:"✅", color:C.green  },
  invite_declined:    { label:"Avslo invitasjon",        icon:"❌", color:C.red    },
  profile_updated:    { label:"Oppdaterte profil",       icon:"👤", color:C.blue   },
  password_changed:   { label:"Byttet passord",          icon:"🔒", color:C.gold   },
  broadcast_sent:     { label:"Sendte kringkasting",     icon:"📢", color:C.magenta},
  todo_added:         { label:"La til todo",             icon:"➕", color:C.green  },
  todo_completed:     { label:"Fullførte todo",          icon:"☑️", color:C.green  },
  todo_deleted:       { label:"Slettet todo",            icon:"🗑",  color:C.red    },
  availability_added: { label:"La til blokkert periode", icon:"📅", color:C.gold   },
  availability_removed:{ label:"Fjernet blokkert periode",icon:"📅", color:C.muted },
  event_created:      { label:"Opprettet event",         icon:"🎉", color:C.orange },
  event_updated:      { label:"Oppdaterte event",        icon:"✏️", color:C.orange },
};

// ── Logg-funksjon (eksporteres og brukes i App.js) ──
export async function logActivity(user, action, details = {}, page = null) {
  if (!user?.id) return;
  await supabase.from("activity_log").insert({
    user_id:   user.id,
    user_name: user.name,
    action,
    details,
    page,
  });
}

// ── Formater tidspunkt ──
function fmtTime(ts) {
  const d = new Date(ts), now = new Date();
  const diff = Math.floor((now - d) / 60000);
  if (diff < 1)    return "Akkurat nå";
  if (diff < 60)   return `${diff}m siden`;
  if (diff < 1440) return `${Math.floor(diff / 60)}t siden`;
  if (diff < 10080)return `${Math.floor(diff / 1440)}d siden`;
  return d.toLocaleDateString("nb-NO", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" });
}

// ── Enkelt aktivitetskort ──
function ActivityItem({ item }) {
  const cfg = ACTION_CFG[item.action] || { label: item.action, icon:"•", color:C.muted };
  const details = item.details || {};
  return (
    <div style={{ display:"flex", gap:10, padding:"10px 0", borderBottom:`0.5px solid ${C.border}` }}>
      <div style={{ fontSize:16, width:24, textAlign:"center", flexShrink:0, marginTop:1 }}>{cfg.icon}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <span style={{ fontSize:13, fontWeight:500, color:cfg.color }}>{cfg.label}</span>
          {details.event_name && <span style={{ fontSize:12, color:C.muted }}>· {details.event_name}</span>}
          {details.page_label && <span style={{ fontSize:12, color:C.muted }}>· {details.page_label}</span>}
          {details.broadcast_title && <span style={{ fontSize:12, color:C.muted }}>· {details.broadcast_title}</span>}
          {details.todo_text && <span style={{ fontSize:12, color:C.muted }}>· {details.todo_text}</span>}
        </div>
        <div style={{ fontSize:11, color:"#444", marginTop:2 }}>{fmtTime(item.created_at)}</div>
      </div>
    </div>
  );
}

// ── Feed for én bruker (brukes i Team-siden) ──
export function UserActivityFeed({ userId, limit = 10 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    supabase
      .from("activity_log")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit)
      .then(({ data }) => { setItems(data || []); setLoading(false); });
  }, [userId, limit]);

  if (loading) return <div style={{ fontSize:12, color:C.muted, padding:"8px 0" }}>Laster aktivitet...</div>;
  if (items.length === 0) return <div style={{ fontSize:12, color:C.muted, padding:"8px 0" }}>Ingen aktivitet registrert ennå.</div>;

  return (
    <div>
      {items.map(item => <ActivityItem key={item.id} item={item} />)}
    </div>
  );
}

// ── Admin-side: full aktivitetslogg ──
export function ActivityPage({ users, isMobile }) {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filterUser, setFilterUser] = useState("all");
  const [filterAction, setFilterAction] = useState("all");
  const [search, setSearch]     = useState("");
  const [page, setPage]         = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => {
    let q = supabase
      .from("activity_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (filterUser !== "all")   q = q.eq("user_id", filterUser);
    if (filterAction !== "all") q = q.eq("action", filterAction);
    q.then(({ data }) => { setItems(data || []); setLoading(false); });
  }, [filterUser, filterAction]);

  const filtered = items.filter(i => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      i.user_name?.toLowerCase().includes(s) ||
      ACTION_CFG[i.action]?.label.toLowerCase().includes(s) ||
      JSON.stringify(i.details).toLowerCase().includes(s)
    );
  });

  const paginated = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = paginated.length < filtered.length;

  // Gruppert per dag
  const grouped = paginated.reduce((acc, item) => {
    const day = new Date(item.created_at).toLocaleDateString("nb-NO", { weekday:"long", day:"numeric", month:"long" });
    if (!acc[day]) acc[day] = [];
    acc[day].push(item);
    return acc;
  }, {});

  return (
    <div style={{ paddingBottom: isMobile ? 80 : 0, maxWidth: 720 }}>
      {gradBar}
      <div style={{ fontSize:20, fontWeight:500, marginBottom:20 }}>Aktivitet</div>

      {/* Filtre */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        <input
          style={inp({ flex:1, minWidth:160 })}
          placeholder="Søk..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select style={inp({ width:"auto" })} value={filterUser} onChange={e => { setFilterUser(e.target.value); setPage(1); }}>
          <option value="all">Alle brukere</option>
          {(users||[]).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select style={inp({ width:"auto" })} value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }}>
          <option value="all">Alle handlinger</option>
          {Object.entries(ACTION_CFG).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {loading && <div style={{ color:C.muted, textAlign:"center", padding:40 }}>Laster...</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ ...card({ textAlign:"center", padding:40 }) }}>
          <div style={{ fontSize:14, color:C.muted }}>Ingen aktivitet funnet</div>
        </div>
      )}

      {!loading && Object.entries(grouped).map(([day, dayItems]) => (
        <div key={day} style={{ marginBottom:20 }}>
          <div style={{ fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:0.5, marginBottom:8 }}>{day}</div>
          <div style={card({ padding:"0 16px" })}>
            {dayItems.map(item => (
              <div key={item.id} style={{ display:"flex", gap:10, padding:"10px 0", borderBottom:`0.5px solid ${C.border}` }}>
                <div style={{ fontSize:16, width:24, textAlign:"center", flexShrink:0, marginTop:1 }}>
                  {ACTION_CFG[item.action]?.icon || "•"}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                    <span style={{ fontSize:13, fontWeight:500, color:"#ccc" }}>{item.user_name}</span>
                    <span style={{ fontSize:12, color:ACTION_CFG[item.action]?.color || C.muted }}>
                      {ACTION_CFG[item.action]?.label || item.action}
                    </span>
                    {item.details?.event_name && <span style={{ fontSize:12, color:C.muted }}>· {item.details.event_name}</span>}
                    {item.details?.page_label && <span style={{ fontSize:12, color:C.muted }}>· {item.details.page_label}</span>}
                    {item.details?.broadcast_title && <span style={{ fontSize:12, color:C.muted }}>· {item.details.broadcast_title}</span>}
                    {item.details?.todo_text && <span style={{ fontSize:12, color:C.muted }}>· {item.details.todo_text}</span>}
                  </div>
                  <div style={{ fontSize:11, color:"#444", marginTop:2 }}>{fmtTime(item.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {hasMore && (
        <button style={{ ...btn("ghost"), width:"100%", marginTop:8 }} onClick={() => setPage(p => p + 1)}>
          Last flere
        </button>
      )}
    </div>
  );
}